import { useTranslation } from 'react-i18next';
import CTASubnav from 'typescript/components/cta_subnav/cta_subnav';
import { useProductContext } from 'typescript/pages/product/contexts/product_context';
import CDP_EVENT_NAMES from 'typescript/constants/cdp_events';

import { trackCdp } from 'utilities/tracking';
import { buildSignUpLink } from 'utilities/links';
import { getLocalePath } from 'utilities/locales';

// todo: move this to a shared interface file eventually maybe
interface NavItem {
  id: string;
  url: string;
  title: string;
}

// Arms of SubnavCtaExperiment (pw-signups-shop-nav-ctas-v1-bucketing). Any other
// value (control, ineligible, holdout, or a missing experiment) renders the
// default Add to cart experience.
const SUBNAV_CTA_VARIATION = {
  VARIANT_B: 'variant_b', // Contact sales (secondary) + Add to cart (primary)
  VARIANT_C: 'variant_c', // Get started (secondary) + Contact sales (primary)
};

const ProductPageCTASubnav = () => {
  const { t } = useTranslation();
  const {
    currentProduct,
    selection,
    onUpdateItemQuantity,
    isUpdateItemQuantityLoading,
    bopisPostalCode,
    bopisPickupLocation,
    experiments,
  } = useProductContext();

  // since we already have the contentful cta_subnav passed to the frontend
  // we can parse the json string to get the title and navItems array
  // this might need to be changed in the future if we stop pulling the subnav data from contentful
  const ctaSubnav = JSON.parse(currentProduct.cta_subnav);
  const title = ctaSubnav?.title || currentProduct?.name;

  // only 1pp have nav items (overview and specs)
  const navItems: NavItem[] = ctaSubnav?.navItems || [];

  const subnavCtaVariation = experiments?.subnavCtaExperiment?.variation;
  const showContactSalesPlusAtc =
    subnavCtaVariation === SUBNAV_CTA_VARIATION.VARIANT_B;
  const showContactSalesPlusGetStarted =
    subnavCtaVariation === SUBNAV_CTA_VARIATION.VARIANT_C;

  // Contact sales lives on public web at the same host; keep it locale-aware so
  // /us/en and /us/es each land on their own page. Get started reuses the shared
  // hardware-shop signup link builder.
  const contactSalesUrl = `${getLocalePath()}/sales/contact`;
  const getStartedUrl = buildSignUpLink({
    returnTo: false,
    step: null,
    pricingPlanToken: null,
  });

  const trackClick = (CDPEventName: string, linkName?: string) => {
    trackCdp({
      name: CDPEventName,
      params: {
        feature: 'shop',
        productKey: currentProduct.product_key,
        linkName: linkName || '',
      },
    });
  };

  // Emits the allowlisted public-web `click_link` event (see cdp_events.ts) so a
  // sub-nav CTA click lands in public_web_clicks next to add-to-cart. link_text
  // distinguishes the CTA; the product slug rides on link_id.
  const trackNavCtaClick = (linkText: string) => {
    const evt = CDP_EVENT_NAMES.PRODUCT_PAGE.SUBNAV_CTA_CLICK;
    trackCdp({
      name: evt.EVENT_NAME,
      params: {
        feature: 'shop',
        link_parent_id: evt.LINK_PARENT_ID_NAV,
        link_text: linkText,
        link_id: currentProduct.slug,
      },
    });
  };

  const onAddToCartClick = () => {
    trackClick(CDP_EVENT_NAMES.PRODUCT_PAGE.CTA_SUBNAV.CLICK_PRIMARY);

    // GRW-816: emit the allowlisted public-web `click_link` event so the sub-nav
    // add-to-cart click lands in public_web_clicks. Nav vs body is parsed off
    // link_parent_id; the product slug rides on link_id (the only property that
    // projects through to public_web_clicks.LINK_ID) so ATC can be cut by
    // product in the same table.
    const atc = CDP_EVENT_NAMES.PRODUCT_PAGE.ADD_TO_CART_CLICK;
    trackCdp({
      name: atc.EVENT_NAME,
      params: {
        feature: 'shop',
        link_parent_id: atc.LINK_PARENT_ID_NAV,
        link_text: atc.LINK_TEXT,
        link_id: currentProduct.slug,
      },
    });

    onUpdateItemQuantity({
      variantToken: currentProduct.variant_token,
      productName: currentProduct.name,
      quantity: selection.quantity,
      addOnIds: Object.values(selection.addOns),
      trackingData: currentProduct.tracking_data,
      bopisLocationId: bopisPickupLocation?.location_id,
      bopisSearchPostalCode: bopisPostalCode,
      source: 'pdp-cta-subnav',
    });
  };

  const onContactSalesClick = (rankEvent: string) => {
    trackClick(rankEvent, 'Contact sales');
    trackNavCtaClick(
      CDP_EVENT_NAMES.PRODUCT_PAGE.SUBNAV_CTA_CLICK.LINK_TEXT.CONTACT_SALES,
    );
  };

  const onGetStartedClick = () => {
    trackClick(
      CDP_EVENT_NAMES.PRODUCT_PAGE.CTA_SUBNAV.CLICK_SECONDARY,
      'Get started',
    );
    trackNavCtaClick(
      CDP_EVENT_NAMES.PRODUCT_PAGE.SUBNAV_CTA_CLICK.LINK_TEXT.GET_STARTED,
    );
  };

  // CTASubnav.Actions extracts its Primary/Secondary children by component type,
  // so these must be direct children (an array flattens; a Fragment would hide
  // them). Slot layout by arm: secondary (left) then primary (right).
  return (
    <CTASubnav>
      <CTASubnav.Title>{title}</CTASubnav.Title>
      <CTASubnav.Navigation>
        {navItems.map((item) => (
          <CTASubnav.Link
            key={item.id}
            href={item.url}
            onClick={() =>
              trackClick(
                CDP_EVENT_NAMES.PRODUCT_PAGE.CTA_SUBNAV.CLICK_LINK,
                item.title,
              )
            }
          >
            {item.title}
          </CTASubnav.Link>
        ))}
      </CTASubnav.Navigation>
      <CTASubnav.Actions>
        {showContactSalesPlusAtc && (
          <CTASubnav.Secondary
            href={contactSalesUrl}
            onClick={() =>
              onContactSalesClick(
                CDP_EVENT_NAMES.PRODUCT_PAGE.CTA_SUBNAV.CLICK_SECONDARY,
              )
            }
          >
            {t('productPage.cta.contactSales')}
          </CTASubnav.Secondary>
        )}
        {showContactSalesPlusGetStarted && (
          <CTASubnav.Secondary href={getStartedUrl} onClick={onGetStartedClick}>
            {t('productPage.cta.getStarted')}
          </CTASubnav.Secondary>
        )}
        {showContactSalesPlusGetStarted ? (
          <CTASubnav.Primary
            href={contactSalesUrl}
            onClick={() =>
              onContactSalesClick(
                CDP_EVENT_NAMES.PRODUCT_PAGE.CTA_SUBNAV.CLICK_PRIMARY,
              )
            }
          >
            {t('productPage.cta.contactSales')}
          </CTASubnav.Primary>
        ) : (
          <CTASubnav.Primary
            onClick={onAddToCartClick}
            disabled={isUpdateItemQuantityLoading}
          >
            {t('productPage.addToCart')}
          </CTASubnav.Primary>
        )}
      </CTASubnav.Actions>
    </CTASubnav>
  );
};

export default ProductPageCTASubnav;
